import { Component, Vue } from "vue-property-decorator";
import { IAuthService, ISyncService } from "@/services";
import { inject } from "inversify-props";
import { eventBus, mcEvents, mcLogger } from "@/utils";
import { types } from "@/store";

@Component
export default class MCSync extends Vue {
  @inject() private readonly authService!: IAuthService;
  @inject() private readonly syncService!: ISyncService;
  private readonly console: Console = mcLogger.getConsole(
    this.constructor.name,
    mcLogger.colors.McSync
  );

  get isOnline() {
    return this.$store.getters[types.state.isOnline];
  }

  async isAuthenticated() {
    return this.authService.isAuthenticated()
    .catch(err =>
    {
      // Default to false if there is an error
      this.console.error('An error occured when checking if authenticated. Returning false.');
      this.console.info('Error:', err); // Don't want to expose errors to users
      return false;
    });
  }

  created() {
    eventBus.$on(mcEvents.loggedIn, this.checkSync);
    eventBus.$on(mcEvents.itemSaved, this.checkSync);
    eventBus.$on(mcEvents.linesMerged, this.checkSync);
    eventBus.$on(mcEvents.selectedDashboardSite, this.joinSignalRGroup);
    eventBus.$on(mcEvents.enteredCountingWorkflow, this.joinSignalRGroup);
    eventBus.$on(mcEvents.deselectedDashboardSite, this.leaveSignalRGroup);
    eventBus.$on(mcEvents.exitedCountingWorkflow, this.leaveSignalRGroup);
    eventBus.$on(mcEvents.receiveUpdatedCount, this.updateIsSyncing);
  }

  beforeDestroy() {
    eventBus.$off(mcEvents.loggedIn);
    eventBus.$off(mcEvents.itemSaved);
    eventBus.$off(mcEvents.linesMerged);
    eventBus.$off(mcEvents.selectedDashboardSite);
    eventBus.$off(mcEvents.enteredCountingWorkflow);
    eventBus.$off(mcEvents.deselectedDashboardSite);
    eventBus.$off(mcEvents.exitedCountingWorkflow);
    eventBus.$off(mcEvents.receiveUpdatedCount);
  }

  async checkSync() {
    // attempt a sync any time we log in
    /* Made this sync up, then down to quick-fix: https://colyar.atlassian.net/browse/MC-1318
     * Ideally, we want the revisions to be logged in chronological order based on the edit times. Not the sync times.
     * Right now (07/10/2020), if someone make edits while offline, and another person makes changes while online AFTER the offline person, then the offline
     * person comes back online, the offline edits get synced up and are seen as the latest revision.
     */
    const isAuthenticated = await this.isAuthenticated();
    if (isAuthenticated) {
      await this.syncService.mergeServingLines();
      await this.syncService.syncUp();
      await this.syncService.syncAllLocalMealsDown();
    }
  }

  async handleOfflineChange(status) {
    // attempt a full data sync any time we regain connectivity
    this.checkSync();

    // reset auth service in case we came back online and have expired auth token
    this.authService.checkRouteAuth();
  }

  /** Set the IsSyncing status to true, wait a second, then set it to false */
  async updateIsSyncing() {
    this.$store.dispatch(types.actions.setIsSyncing, true);

    setTimeout(() => {
      this.$store.dispatch(types.actions.setIsSyncing, false);
    }, 1000);
  }

  async joinSignalRGroup(groupId: string) {
    this.syncService.joinSignalRGroup(groupId);
  }

  async leaveSignalRGroup(groupId: string) {
    this.syncService.leaveSignalRGroup(groupId);
  }
}
